/**
 * Pointage d'un créneau (début / fin / pause / reprise), partagé par le planning et le bandeau « En pause ».
 * Hors ligne ou Dolibarr indisponible, le pointage est gardé sur le téléphone (offlineQueue) avec son heure réelle
 * et transmis automatiquement au retour du réseau / de Dolibarr, dans l'ordre.
 */
import { toast } from 'sonner';
import { easerPost, getPosition, errorMessage } from './easerApi';
import { enqueue, shouldQueue, isNetworkError, notifyClockChanged, CLOCK_TYPES } from './offlineQueue';

export const CLOCK_WORDS = {
  start: { done: 'Début pointé', queued: 'Le début de prestation est enregistré' },
  end: { done: 'Fin de prestation transmise', queued: 'La fin de prestation est enregistrée' },
  pause: { done: 'Pause enregistrée (fin pointée dans Dolibarr)', queued: 'La pause est enregistrée' },
  resume: { done: 'Prestation reprise (début pointé dans Dolibarr)', queued: 'La reprise de prestation est enregistrée' },
};

/**
 * Clock `action` on slot `ev` ({id, label}).
 * mustQueue: an earlier pointage of this slot still waits on the device -> this one is queued too (order kept).
 * Returns { queued } | { queued: false, data } | { error: true } | null (401, onUnauthorized called).
 */
export async function clockSlot({ ev, action, mustQueue = false, onUnauthorized, onWebSessionLost }) {
  const words = CLOCK_WORDS[action];
  const pos = await getPosition();
  const queue = () => enqueue({ type: CLOCK_TYPES[action], eventId: ev.id, label: ev.label, payload: { action, ...pos } });
  if (mustQueue) {
    await queue();
    toast.info(`${words.queued} à la suite du pointage en attente : tout sera transmis à Dolibarr dans l'ordre.`, { duration: 8000 });
    notifyClockChanged();
    return { queued: true };
  }
  try {
    const res = await easerPost(`/api/easer/events/${ev.id}/clock`, { action, ...pos });
    if (res.data.myschedule) {
      toast.success(`${words.done} · vérifié dans Dolibarr (MySchedule)`, { duration: 6000 });
    } else {
      toast.warning(`${words.done} (vérifié dans Dolibarr) — ${res.data.warning || 'MySchedule non synchronisé'}`, { duration: 8000 });
      onWebSessionLost?.();
    }
    notifyClockChanged();
    return { queued: false, data: res.data };
  } catch (err) {
    if (err?.response?.status === 401) { onUnauthorized?.(); return null; }
    if (shouldQueue(err)) {
      await queue();
      toast.warning(isNetworkError(err)
        ? `Pas de réseau : ${words.queued.toLowerCase()} sur le téléphone et sera transmis(e) à Dolibarr automatiquement dès le retour du réseau.`
        : `Dolibarr indisponible : ${words.queued.toLowerCase()} sur le téléphone et sera transmis(e) automatiquement dès le retour de Dolibarr.`, { duration: 9000 });
      notifyClockChanged();
      return { queued: true };
    }
    toast.error(errorMessage(err), { duration: 8000 });
    return { error: true };
  }
}
